import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchReactionPeople } from "services/posts.service";
import { ReactionPerson } from "types/Post";
import { REACTION_EMOJI } from "../utils/reactions";
import { UserNameWithCompany } from "./UserNameWithCompany";

interface ReactionPeopleDropdownProps {
  companyId: string;
  postId: string;
  /** People from this company need no label; people from another one show it. */
  postCompanyId: string;
  /** The button that opens the list; a click on it is not a click outside. */
  openerRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}

/** Who reacted to a post, and with what. It is mounted only while open and reads the list anew each time. */
export function ReactionPeopleDropdown({
  companyId,
  postId,
  postCompanyId,
  openerRef,
  onClose,
}: ReactionPeopleDropdownProps) {
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  // null while loading.
  const [people, setPeople] = useState<ReactionPerson[] | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let isStillWanted = true;
    fetchReactionPeople(companyId, postId)
      .then((loaded) => {
        if (isStillWanted) setPeople(loaded);
      })
      .catch((err) => {
        console.error("Error loading who reacted:", err);
        if (isStillWanted) setHasFailed(true);
      });
    return () => {
      isStillWanted = false;
    };
  }, [companyId, postId]);

  useEffect(() => {
    const closeOnClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!dropdownRef.current?.contains(target) && !openerRef.current?.contains(target)) onClose();
    };
    document.addEventListener("mousedown", closeOnClickOutside);
    return () => document.removeEventListener("mousedown", closeOnClickOutside);
  }, [openerRef, onClose]);

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 z-50 mt-2 max-h-72 w-72 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
    >
      {hasFailed ? (
        <p className="p-4 text-center text-sm text-gray-500">{t("news_reactions_load_failed")}</p>
      ) : people === null ? (
        <div className="space-y-2 p-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-2 p-2">
              <div className="h-8 w-8 flex-shrink-0 animate-pulse rounded-full bg-gray-300"></div>
              <div className="h-4 flex-1 animate-pulse rounded bg-gray-300"></div>
            </div>
          ))}
        </div>
      ) : people.length === 0 ? (
        <p className="p-4 text-center text-sm text-gray-500">{t("news_reactions_none_yet")}</p>
      ) : (
        <ul className="py-2">
          {people.map((person) => {
            const name = person.name || t("feed_user");
            return (
              <li key={person.userId} className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                  {name.charAt(0).toUpperCase()}
                </div>
                <p className="min-w-0 flex-1 truncate text-sm text-gray-800">
                  <UserNameWithCompany
                    name={name}
                    companyName={person.companyName}
                    companyId={person.companyId}
                    ownCompanyId={postCompanyId}
                  />
                </p>
                <span className="flex-shrink-0 text-sm">{person.reactions.map((reaction) => REACTION_EMOJI[reaction]).join(" ")}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
